/**
 * The **open editor** for one field row — the half of the row that appears in place
 * of the read-out once the coordinator clicks it.
 *
 * Split out of `attribute-grid.tsx`, which had grown past the 300-line
 * guideline by carrying the grid, the read-out and this control in one file. The
 * seam is the row's own two halves: `AttributeReadOut` renders the value, this
 * renders the control that replaces it, and the grid owns which of the two shows.
 *
 * Three behaviours are load-bearing, and are why this is one branch per editor kind
 * rather than one input with props:
 *
 * · **The editor opens in the cell the read-out occupied**, so the value never jumps
 *   sideways on click — hence the matched `px-1.5` and the full-width, left-aligned
 *   select trigger.
 * · **A select opens on mount** (`defaultOpen`), so clicking a value shows the
 *   options immediately instead of parking a closed control under the pointer.
 * · **Tab is taken over** so it commits and moves to the next field, rather than
 *   escaping the card after a single correction.
 */

import { useLayoutEffect, useRef } from 'react';
import type { PostalAddress } from '@/lib/services/matters/types';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { fromIsoDateOnly, toIsoDateOnly } from '@/lib/formatters/date-only';
import { useCurrencyInput } from '@/components/inventory/inventory-form-card/shared/currency-input';
import {
  ATTRIBUTE_EDITOR_BARE,
  ATTRIBUTE_INSET,
  ATTRIBUTE_LABEL_TEXT,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_MUTED_INK,
  ATTRIBUTE_VALUE_INK,
  ATTRIBUTE_VALUE_SLOT,
  ATTRIBUTE_VALUE_TEXT
} from './attribute-tokens';
import { AttributeAddressEditor } from './attribute-address-editor';
import { AttributeMultiSelectEditor } from './attribute-multi-select-editor';
import { AttributeSelectWithCustomEditor } from './attribute-select-with-custom-editor';
import { AttributeValueConfirmationEditor } from './attribute-value-confirmation-editor';
import { AttributeMarketFigureEditor } from './attribute-market-figure-editor';
import { AttributeNameEditor } from './attribute-name-editor';
import { AttributeBankAccountEditor } from './attribute-bank-account-editor';
import { AttributePrincipalMembersEditor } from './attribute-principal-members-editor';
import { attributePlaceholder } from './attribute-placeholder';
import type {
  AttributeBankAccount,
  AttributeDraft,
  AttributeMarketFigure,
  AttributeNameParts,
  AttributeOption,
  AttributePrincipalMembers,
  AttributeSpec,
  AttributeValueConfirmation
} from './attribute-types';

/**
 * **Which editor kinds open in a POPOVER rather than in the row itself.**
 *
 * The composite kinds, each of which returns early below with a `Popover` whose
 * trigger sits in the row's own slot: the panel floats over the surface, so the
 * editor never uses the row's width and the row has no reason to change it.
 *
 * Exported for `AttributeGrid`'s resting cap (VALE-1247). That cap hugs a
 * `fullWidth` row's target to its value and used to be released on open for
 * every kind — right for an inline control, which is typed into and does want
 * the span it was given, and wrong for every kind here, where the only visible
 * effect was the row snapping out to the full width of the surface the instant
 * it was clicked. Kept in this module because this is where the branches are:
 * a kind added below without being added here would release the cap again, and
 * the two lists sitting in one file is the cheapest way to notice.
 */
export const POPOVER_EDITOR_KINDS = [
  'address',
  'multiSelect',
  'principalAndMembers',
  'valueWithConfirmation',
  'marketFigure',
  'bankAccount',
  'name',
  'selectWithCustom'
];

/** Whether this row's editor floats over the surface instead of filling the row. */
export function rendersEditorInPopover(field: {
  editor?: { kind?: string };
}): boolean {
  return POPOVER_EDITOR_KINDS.includes(field.editor?.kind ?? '');
}

/**
 * The vocabulary a `boolean` row's picker offers.
 *
 * `'yes'` / `'no'` strings rather than a JS boolean so the draft stays one type
 * across every editor kind — the grid's open / revert / commit machinery never
 * looks inside a draft, and each spec's `toPatch` narrows it.
 */
const BOOLEAN_OPTIONS: AttributeOption[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' }
];

export function AttributeFieldControl({
  field,
  draft,
  unified = true,
  onDraft,
  onRevert,
  onCommit,
  onStep
}: {
  field: AttributeSpec<unknown>;
  draft: AttributeDraft;
  /**
   * **Render bare, because the cell is drawing the chrome** (LF-6359 /
   * [ATTR-9]).
   *
   * On the grid's `unified` path {@link AttributeValueCell} carries the ring,
   * the `slate-50` fill and the radius, so every control below drops its own
   * border — a second border inside the first is the double outline the token
   * {@link ATTRIBUTE_EDITOR_BARE} exists to prevent.
   *
   * It also selects the *story's* date editor rather than the native one; see
   * the `date` branch below.
   *
   * **Defaults to `true`** (LF-6388 / [ATTR-10]): the unified treatment is the
   * treatment. It defaulted to `false` while `AttributeGrid`'s `refined` was an
   * opt-in; the flag inverted into `legacyChrome` and one call site holds it
   * back — see that prop.
   */
  unified?: boolean;
  onDraft: (next: AttributeDraft) => void;
  onRevert: () => void;
  onCommit: (value: AttributeDraft) => void;
  onStep: (value: AttributeDraft, delta: number) => void;
}) {
  if (field.editor.kind === 'address') {
    return (
      <AttributeAddressEditor
        label={field.label}
        // `AttributeDraft` widened past `string | PostalAddress | null` for
        // `name`'s own draft (VALE-129) — this branch only ever receives one of
        // the two non-string shapes, decided by `field.editor.kind`, so the cast
        // is safe rather than a narrowing TS can do on its own.
        address={
          typeof draft === 'string' ? null : (draft as PostalAddress | null)
        }
        unified={unified}
        stateOptions={field.editor.stateOptions}
        onDraft={onDraft}
        onRevert={onRevert}
        onCommit={onCommit}
      />
    );
  }

  if (field.editor.kind === 'multiSelect') {
    return (
      <AttributeMultiSelectEditor
        label={field.label}
        value={typeof draft === 'string' ? draft : ''}
        options={field.editor.options}
        onDraft={onDraft}
        onRevert={onRevert}
        onCommit={onCommit}
      />
    );
  }

  if (field.editor.kind === 'principalAndMembers') {
    return (
      <AttributePrincipalMembersEditor
        label={field.label}
        // Same narrowing the two branches above do: `AttributeDraft` carries
        // every kind's shape, and which one this branch receives is decided by
        // `field.editor.kind` rather than by anything TS can see.
        value={
          typeof draft === 'string'
            ? null
            : (draft as AttributePrincipalMembers | null)
        }
        principalLabel={field.editor.principalLabel}
        membersLabel={field.editor.membersLabel}
        options={field.editor.options}
        noneLabel={field.editor.noneLabel}
        onDraft={onDraft}
        onRevert={onRevert}
        onCommit={onCommit}
      />
    );
  }

  if (field.editor.kind === 'valueWithConfirmation') {
    return (
      <AttributeValueConfirmationEditor
        label={field.label}
        // Same narrowing the popover branches above do: `AttributeDraft` carries
        // every kind's shape and `field.editor.kind` decides which one arrives.
        value={
          draft && typeof draft === 'object' && 'confirmed' in draft
            ? (draft as AttributeValueConfirmation)
            : null
        }
        amountLabel={field.editor.amountLabel}
        signed={field.editor.signed}
        documents={field.editor.documents}
        documentsPlaceholder={field.editor.documentsPlaceholder}
        provenance={field.editor.provenance}
        provenanceTone={field.editor.provenanceTone}
        quickFill={field.editor.quickFill}
        components={field.editor.components}
        deriveAmount={field.editor.deriveAmount}
        derivedAmountCaption={field.editor.derivedAmountCaption}
        onDraft={onDraft}
        onRevert={onRevert}
        onCommit={onCommit}
      />
    );
  }

  if (field.editor.kind === 'marketFigure') {
    return (
      <AttributeMarketFigureEditor
        label={field.label}
        // Same narrowing the popover branches above do: `AttributeDraft` carries
        // every kind's shape and `field.editor.kind` decides which one arrives.
        value={
          draft && typeof draft === 'object' && 'manual' in draft
            ? (draft as AttributeMarketFigure)
            : null
        }
        valueLabel={field.editor.valueLabel}
        priceLabel={field.editor.priceLabel}
        provenance={field.editor.provenance}
        derivation={field.editor.derivation}
        deriveValue={field.editor.deriveValue}
        manualEntry={field.editor.manualEntry}
        fixedNote={field.editor.fixedNote}
        onDraft={onDraft}
        onRevert={onRevert}
        onCommit={onCommit}
      />
    );
  }

  if (field.editor.kind === 'bankAccount') {
    return (
      <AttributeBankAccountEditor
        label={field.label}
        // Same narrowing every popover branch here does: `AttributeDraft`
        // carries every kind's shape, and which one arrives is decided by
        // `field.editor.kind` rather than by anything TS can see. `'bsb' in
        // draft` is not a usable discriminator — every part of this draft is
        // optional, so an account holding only a verified flag has none of the
        // keys a type guard could key on.
        account={
          draft && typeof draft === 'object'
            ? (draft as AttributeBankAccount)
            : null
        }
        accountName={field.editor.accountName}
        verified={field.editor.verified}
        verificationNote={field.editor.verificationNote}
        onDraft={onDraft}
        onRevert={onRevert}
        onCommit={onCommit}
      />
    );
  }

  if (field.editor.kind === 'name') {
    return (
      <AttributeNameEditor
        label={field.label}
        name={
          typeof draft === 'string'
            ? null
            : (draft as AttributeNameParts | null)
        }
        titleOptions={field.editor.titleOptions}
        onDraft={onDraft}
        onRevert={onRevert}
        onCommit={onCommit}
      />
    );
  }

  /*
    A `boolean` opens the same two-option picker a `select` does (LF-6303).

    Handled alongside `select` rather than given its own branch because the
    editors are genuinely the same control over a two-value vocabulary — the two
    kinds differ at the **read-out**, which is where §1.8's argument lives, not
    here. Keeping one branch is also what stops the two drifting: a fix to the
    picker's commit-on-choose or dismiss-to-revert behaviour lands on both.

    A one-click live control (a switch, a segmented pair) is the refinement
    LF-6186 round 2 explored and {@link BooleanControl} names, but *which* of
    them the Contacts drawer takes is [ATTR-5]'s design call — so the selector
    lands with that decision rather than shipping four renderings now, one of
    which (a checkbox in the value cell) the treatment has already rejected.
  */
  /*
    A closed vocabulary with a free-text escape hatch (VALE-1227). Its own
    branch rather than a flag on `select` below, because it is genuinely two
    controls with two commit contracts and which one shows is *state* — see the
    component, which owns the switch.
  */
  if (field.editor.kind === 'selectWithCustom') {
    return (
      <AttributeSelectWithCustomEditor
        label={field.label}
        draft={draft}
        options={field.editor.options}
        customLabel={field.editor.customLabel}
        customPlaceholder={field.editor.customPlaceholder}
        unified={unified}
        onDraft={onDraft}
        onRevert={onRevert}
        onCommit={onCommit}
        onStep={onStep}
      />
    );
  }

  if (field.editor.kind === 'select' || field.editor.kind === 'boolean') {
    const options: AttributeOption[] =
      field.editor.kind === 'select' ? field.editor.options : BOOLEAN_OPTIONS;
    /*
      `SelectValue` mirrors the chosen ITEM's rendered children, so an option
      carrying a second line would draw both of them into the trigger, three
      wrapped lines deep in a cell sized for one. Naming the label explicitly is
      the only thing that stops it: `textValue` governs the built-in typeahead
      and nothing else (VALE-1408).
    */
    const selectedLabel = options.find(
      (option) => option.value === draft
    )?.label;
    return (
      <AttributeSelectEditor
        field={field}
        draft={draft}
        options={options}
        selectedLabel={selectedLabel}
        unified={unified}
        onRevert={onRevert}
        onCommit={onCommit}
      />
    );
  }

  const text = typeof draft === 'string' ? draft : '';

  if (field.editor.kind === 'money') {
    return (
      <AttributeMoneyEditor
        field={field}
        text={text}
        unified={unified}
        onDraft={onDraft}
        onRevert={onRevert}
        onCommit={onCommit}
        onStep={onStep}
      />
    );
  }

  if (field.multiline) {
    return (
      <AttributeLongTextEditor
        field={field}
        text={text}
        unified={unified}
        onDraft={onDraft}
        onRevert={onRevert}
        onCommit={onCommit}
        onStep={onStep}
      />
    );
  }

  if (unified && field.editor.kind === 'date') {
    return (
      <AttributeDateEditor
        field={field}
        text={text}
        onRevert={onRevert}
        onCommit={onCommit}
        onStep={onStep}
      />
    );
  }

  const inputType =
    field.editor.kind === 'number'
      ? 'number'
      : field.editor.kind === 'date'
        ? 'date'
        : 'text';
  return (
    <Input
      autoFocus
      aria-label={field.label}
      type={inputType}
      placeholder={
        field.editor.kind === 'text' ? field.editor.placeholder : undefined
      }
      value={text}
      // Select-all on open: these are corrections more often than additions, so
      // typing should replace rather than append.
      onFocus={(event) => event.currentTarget.select()}
      onChange={(event) => onDraft(event.target.value)}
      onBlur={() => onCommit(text)}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          // Reverting is only half the job — the sheet owns Escape and would
          // dismiss underneath us. Stopping the event here is not enough (Radix
          // listens above React's root), so the drawer holds the other half via
          // `onEditingChange` → `onEscapeKeyDown`.
          event.preventDefault();
          onRevert();
        } else if (event.key === 'Enter') {
          event.preventDefault();
          onCommit(text);
        } else if (event.key === 'Tab') {
          // Taking Tab over is the whole point — the default would leave the card
          // entirely after one correction.
          event.preventDefault();
          onStep(text, event.shiftKey ? -1 : 1);
        }
      }}
      // `ATTRIBUTE_INSET` matches the read-out's, so the text stays exactly
      // where it was when the editor opens over it — the Input's own `px-3`
      // would shift it 6px. That shared inset is precisely why it is a token.
      /*
        `ATTRIBUTE_LEADING` is an arbitrary property since VALE-1505, so it lands
        whatever order it is composed in, the two arms used to order these
        differently to work around tailwind-merge dropping it, and the editors
        that did not know the trick opened 2.5px shorter than their read-out.
        See the token.
      */
      className={cn(
        ATTRIBUTE_VALUE_TEXT,
        ATTRIBUTE_INSET,
        ATTRIBUTE_LEADING,
        unified && ATTRIBUTE_EDITOR_BARE
      )}
    />
  );
}

/**
 * The editor for a `select` row, and for a `boolean` row on the `select`
 * control (LF-6303).
 *
 * Its own component rather than a branch so it can hold one piece of state:
 * **whether a choice was just made**. Radix fires `onValueChange` and then
 * `onOpenChange(false)` for the same click, and the dismiss arm reverts, so a
 * chosen option was immediately followed by a `revert`, which forgot any
 * standing refusal the commit had deliberately kept until its retry landed
 * (`use-attribute-commit.ts`, VALE-349). On a row the server had just refused,
 * picking a new option cleared the refusal a round trip early and the leave
 * guard could wave the drawer through mid-retry. The dismiss arm now stands
 * down after a choice (VALE-1505).
 */
function AttributeSelectEditor({
  field,
  draft,
  options,
  selectedLabel,
  unified = true,
  onRevert,
  onCommit
}: {
  field: AttributeSpec<unknown>;
  draft: AttributeDraft;
  options: AttributeOption[];
  selectedLabel: string | undefined;
  unified?: boolean;
  onRevert: () => void;
  onCommit: (value: AttributeDraft) => void;
}) {
  const choseRef = useRef(false);
  return (
    <Select
      // Opens on mount, so clicking the value shows the options immediately
      // rather than parking a closed control under the pointer.
      defaultOpen
      value={typeof draft === 'string' ? draft : ''}
      onValueChange={(next) => {
        choseRef.current = true;
        onCommit(next);
      }}
      onOpenChange={(next) => {
        if (next) return;
        /*
          Dismissed without choosing: revert rather than commit. The value is
          unchanged either way, but reverting keeps `Escape` and an outside
          click behaving identically. After a choice the commit has already
          decided what the row holds, see the component's doc.

          **The flag is consumed, not latched.** A commit the spec REFUSES
          leaves the editor open (`commitAndClose` closes only on an accepted
          commit), and this arm has no other exit: no `onBlur`, no `onKeyDown`.
          Latching the flag left the row permanently stuck, because every later
          dismissal returned here early and never reverted. Clearing it means
          the next dismissal behaves like a first one (VALE-1505).
        */
        if (choseRef.current) {
          choseRef.current = false;
          return;
        }
        onRevert();
      }}
    >
      {/*
          Full width and left-aligned so the open editor occupies the same cell
          the read-out did, the value must not jump sideways on click.
        */}
      <SelectTrigger
        hideArrow
        aria-label={field.label}
        /*
          **The row's exit when the listbox is shut but the editor is not.**
          A commit the spec refuses leaves the cell in edit mode
          (`commitAndClose` closes only on an accepted commit) while Radix has
          already closed the listbox behind the choice, so `onOpenChange` has
          nothing left to fire and the row had no way back at all. The text and
          textarea arms have carried their own `Escape` for this reason; the
          select arm was the one that did not (VALE-1505).
        */
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return;
          event.stopPropagation();
          choseRef.current = false;
          onRevert();
        }}
        className={cn(
          'h-auto w-full justify-start bg-transparent',
          ATTRIBUTE_VALUE_TEXT,
          ATTRIBUTE_INSET,
          ATTRIBUTE_LEADING,
          ATTRIBUTE_VALUE_INK,
          unified && ATTRIBUTE_EDITOR_BARE
        )}
      >
        {/*
            **The row must not collapse while the picker is open** (VALE-1505).
            `SelectValue` renders nothing for an empty value and this trigger
            is `h-auto`, so an unset `select` row shrank from 26px to its 7px
            of padding the instant it was clicked, measured on the add
            dialog's `Relationship to deceased` and the inventory drawer's
            `Estate type`. The same `Set <label>…` the read-out stood in with
            keeps the row its own height until a choice lands.
          */}
        <span className={cn('truncate', !selectedLabel && ATTRIBUTE_MUTED_INK)}>
          {selectedLabel ?? attributePlaceholder(field.label)}
        </span>
      </SelectTrigger>
      <SelectContent align="start">
        {options.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            className={ATTRIBUTE_VALUE_TEXT}
            // What the built-in typeahead matches on, which would
            // otherwise be the label and its second line run together.
            textValue={option.label}
          >
            {option.description ? (
              <span className="flex flex-col gap-0.5">
                <span>{option.label}</span>
                <span className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK)}>
                  {option.description}
                </span>
              </span>
            ) : (
              option.label
            )}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * The editor for a `money` row (`components/attributes/CLAUDE.md` §3).
 *
 * **The whole reason this is its own branch rather than a `text` field with a
 * `$` glued on is the focus contract**, bound to `useCurrencyInput` rather than
 * re-typed: **while the field is focused the raw string is what the user
 * sees**, and thousands separators are applied only once it blurs.
 * Reformatting on every keystroke eats the trailing `.` of `1234.5` and makes a
 * decimal impossible to type — which on a commit-on-blur surface, where every
 * edit ends in a blur, would be unusable rather than merely annoying.
 *
 * So the draft the grid moves around stays the exact raw string the user
 * typed — `onDraft` and `onCommit` both receive it unparsed, never the
 * comma-formatted display value. The read-out is what applies `formatCurrency`
 * (a spec's own `display`, per the worked example); this control deliberately
 * does not, and the `$` sits outside the input as inert chrome so the caret
 * never has to step over it and a paste cannot land on the wrong side of it.
 */
function AttributeMoneyEditor({
  field,
  text,
  unified = true,
  onDraft,
  onRevert,
  onCommit,
  onStep
}: {
  field: AttributeSpec<unknown>;
  text: string;
  /** Bare, because the cell draws the chrome — see {@link AttributeFieldControl}. */
  unified?: boolean;
  onDraft: (next: AttributeDraft) => void;
  onRevert: () => void;
  onCommit: (value: AttributeDraft) => void;
  onStep: (value: AttributeDraft, delta: number) => void;
}) {
  // `value` only — the hook's own `onChange` callback hands back a *parsed*
  // number, which is not what this control moves around (see above). Its
  // `displayValue` is what this control actually wants: the raw string while
  // focused, comma-formatted once it is not.
  const currencyInput = useCurrencyInput({ value: text });

  return (
    <div className="relative flex min-w-0 flex-1 items-center">
      {/*
        Chrome, not content: it sits in the padding rather than in the value,
        so the caret cannot land before it and a paste cannot end up on the
        wrong side of it. `pl-1.5` matches the read-out's own inset, so the
        figure does not move when the editor opens over it.
      */}
      <span
        aria-hidden
        className={cn(
          ATTRIBUTE_VALUE_TEXT,
          ATTRIBUTE_LEADING,
          'text-muted-foreground pointer-events-none pl-1.5'
        )}
      >
        $
      </span>
      <Input
        autoFocus
        aria-label={field.label}
        /*
          `inputMode="decimal"` rather than `type="number"`: a number input's
          spinner and its locale-dependent parsing both fight the raw-string
          contract above, and on a touch keyboard `decimal` is what actually
          shows a `.`.
        */
        type="text"
        inputMode="decimal"
        value={currencyInput.displayValue}
        onFocus={(event) => {
          // Unformat first, then select on the next frame: focusing swaps "1,500.00" for "1500",
          // and replacing the value drops a selection made before it, so the figure opened
          // focused but not selected and typing appended instead of overriding.
          const input = event.currentTarget;
          // Typed before the frame came round: selecting then would swallow those keystrokes.
          delete input.dataset.typed;
          currencyInput.handleFocus();
          requestAnimationFrame(() => {
            if (document.activeElement === input && !input.dataset.typed) {
              input.select();
            }
          });
        }}
        onChange={(event) => {
          event.currentTarget.dataset.typed = '1';
          // Keep the hook's internal `raw` in step with the grid's own draft —
          // both must hold the identical, unformatted string, or the two
          // diverge the moment either one reformats.
          currencyInput.handleChange(event.target.value);
          onDraft(event.target.value);
        }}
        onBlur={() => {
          currencyInput.handleBlur();
          onCommit(text);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            onRevert();
          } else if (event.key === 'Enter') {
            event.preventDefault();
            onCommit(text);
          } else if (event.key === 'Tab') {
            event.preventDefault();
            onStep(text, event.shiftKey ? -1 : 1);
          }
        }}
        className={cn(
          ATTRIBUTE_VALUE_TEXT,
          ATTRIBUTE_LEADING,
          'h-auto py-1 pl-1',
          unified && ATTRIBUTE_EDITOR_BARE
        )}
      />
    </div>
  );
}

/**
 * The editor for a value that runs to several lines — a `multiline` field
 * (LF-6303 / [ATTR-2]).
 *
 * A single-line `Input` on a long note scrolls sideways under the cursor, so you
 * cannot see the sentence you are editing, which is the one thing the field
 * exists for.
 *
 * **It grows in the value's own slot rather than opening a layer over it.** That
 * is `LongTextEditor`'s `grow`, and it is the default because §1.5 — the editor
 * opens over the value and the value must not move — is the rule the whole
 * treatment rests on. The cost is honest and is the reason `popover` remains in
 * the vocabulary for [ATTR-5] to choose instead: the rows below this one move
 * while you type.
 *
 * **`Enter` inserts a newline here rather than committing.** This is the one
 * field kind where multi-line input is the point, so the single-line editor's
 * `Enter`-to-commit would make a paragraph unwritable. Commit is blur or `Tab`;
 * `Escape` still reverts, and both keep the contract §2 states for every other
 * row.
 */
function AttributeLongTextEditor({
  field,
  text,
  unified = true,
  onDraft,
  onRevert,
  onCommit,
  onStep
}: {
  field: AttributeSpec<unknown>;
  text: string;
  /** Bare, because the cell draws the chrome — see {@link AttributeFieldControl}. */
  unified?: boolean;
  onDraft: (next: AttributeDraft) => void;
  onRevert: () => void;
  onCommit: (value: AttributeDraft) => void;
  onStep: (value: AttributeDraft, delta: number) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  /*
    Auto-grow, measured off the element rather than guessed from a character
    count — a proportional font makes any character-count estimate wrong at some
    width. Reset to `auto` first, or `scrollHeight` reports the height the box
    already has and it can then only ever grow. `useLayoutEffect` so the resize
    lands in the same frame as the keystroke and the box never paints at the
    wrong height.
  */
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);

  return (
    <div className={ATTRIBUTE_VALUE_SLOT} onBlur={() => onCommit(text)}>
      <Textarea
        ref={ref}
        autoFocus
        aria-label={field.label}
        value={text}
        rows={1}
        /*
          The same editing-only format example the single-line `text` editor
          already honours (VALE-743). A `multiline` row is still `kind: 'text'`,
          so the member was always there and simply never read here. VALE-1402
          needed it: `Stored at` is a sentence a coordinator has to be shown the
          shape of ("e.g. With John Smith Solicitors in Bondi, or in a safe at
          home"), which is exactly what the intake form asking the same question
          puts under its own input. Rows with nothing ambiguous to hint at pass
          nothing and render no placeholder, as before.
        */
        placeholder={
          field.editor.kind === 'text' ? field.editor.placeholder : undefined
        }
        onChange={(event) => onDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            // Same two-part job as the single-line editor: the sheet owns
            // Escape and would dismiss underneath us, so the drawer holds the
            // other half via `onEditingChange` → `onEscapeKeyDown`.
            event.preventDefault();
            onRevert();
          } else if (event.key === 'Tab') {
            event.preventDefault();
            onStep(text, event.shiftKey ? -1 : 1);
          }
          // `Enter` is deliberately NOT intercepted — see this function's doc.
        }}
        /*
          `px-1.5` matches the read-out's so the text does not shift on open,
          and `resize-none` because the box sizes itself.

          `min-h-0` is load-bearing: `Textarea` ships an 80px floor sized for a
          standalone form field, which in a 30px attribute row would open a hole
          four rows deep on a one-line note.
        */
        className={cn(
          'min-h-0 resize-none overflow-hidden',
          ATTRIBUTE_VALUE_TEXT,
          ATTRIBUTE_INSET,
          ATTRIBUTE_LEADING,
          unified && ATTRIBUTE_EDITOR_BARE
        )}
      />
    </div>
  );
}

/**
 * **The unified date editor is the story's `DatePicker`, not the native
 * control** (LF-6359 / [ATTR-9] §2).
 *
 * §2 reads as "same root cause as §1", and the chrome half of it is — but the
 * story diverges further than that: `<Input type="date">` renders the browser's
 * own control, whose spinner, placeholder and calendar glyph are the user
 * agent's rather than this surface's, so it looks nothing like any other row in
 * the drawer. `DatePicker` is what the prototype opens, with `yearNavigation`
 * (a date of birth is decades back, and stepping a month at a time to reach it
 * is the reason that prop exists) and `allowInput` (so a date that is already
 * known can still be typed rather than navigated to).
 *
 * The two corrections to its own calendar button are the prototype's, reached
 * through the wrapper rather than by changing `DatePicker` — which is
 * production and correct as it is for its other hosts. The button is pulled in
 * to the cell's edge and sized to the 14px the copy / clear affordances beside
 * it now use, so the three read as one set rather than as one control's
 * furniture next to two of the row's.
 *
 * **The keyboard contract is this component's own, and it is why the branch was
 * extracted** (VALE-1321).
 *
 * The grid's single-line field has always committed on `Enter`, reverted on
 * `Escape` and stepped on `Tab`; this row did none of the three, and it did not
 * commit on blur either. `DatePicker` owns only `ArrowDown` (open the
 * calendar), so all four had to be added around it, and adding the focus
 * effect they depend on needs hooks, which cannot be called inside a branch of
 * {@link AttributeFieldControl} that sits behind a dozen early returns. Hence a
 * component, exactly as the money and long-text editors already are.
 *
 * **Blur is the interesting one.** `DatePicker` portals its calendar out of
 * this subtree, so the naive "focus left, therefore commit" would fire the
 * moment the reader clicks the calendar button, closing the editor out from
 * under the calendar they just opened. The guard below is what makes reaching
 * for the calendar not count as leaving.
 *
 * Committing on blur here changes what *closes*, not what is *stored*:
 * `onDateChange` already commits every complete date and the cleared state as
 * they are typed, so the only value a blur can carry is the one the row opened
 * with. That is a `sameDraft` no-op in the grid's own commit path, which is
 * precisely the point: it closes the editor without writing, and an
 * incomplete date is discarded exactly as `Enter` discards it.
 */
function AttributeDateEditor({
  field,
  text,
  onRevert,
  onCommit,
  onStep
}: {
  field: AttributeSpec<unknown>;
  text: string;
  onRevert: () => void;
  onCommit: (value: AttributeDraft) => void;
  onStep: (value: AttributeDraft, delta: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  /*
    Focus the typed input as the editor opens. Opening a row unmounts the
    read-out that was clicked, so focus otherwise falls back to the document
    body and every key below is dead, which is the state this row actually
    shipped in. The same ref-and-query shape `attribute-boolean.tsx` uses to
    put focus on a live segmented control, for the same reason: the control is
    somebody else's component and there is no prop to ask.
  */
  useLayoutEffect(() => {
    ref.current?.querySelector<HTMLInputElement>('input')?.focus();
  }, []);

  /*
    `fromIsoDateOnly`, never `new Date(text)`: a bare `YYYY-MM-DD` parses as UTC
    midnight, which is the previous calendar day west of Greenwich. The helper is
    the inverse of the `toIsoDateOnly` the commit below already binds to, so the
    round trip is lossless (VALE-1505).
  */
  const parsed = fromIsoDateOnly(text);

  return (
    <div
      ref={ref}
      className="[&_button]:text-muted-foreground [&_button:hover]:bg-muted [&_button:hover]:text-foreground min-w-0 flex-1 [&_button]:right-0 [&_button]:size-5 [&_button_svg]:size-3.5 [&_button_svg]:shrink-0"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          /*
            Same two-part job as the single-line editor: the sheet owns Escape
            and would dismiss underneath us, so the drawer holds the other half
            via `onEditingChange` -> `onEscapeKeyDown`.
          */
          event.preventDefault();
          onRevert();
        } else if (event.key === 'Enter') {
          event.preventDefault();
          onCommit(text);
        } else if (event.key === 'Tab') {
          event.preventDefault();
          onStep(text, event.shiftKey ? -1 : 1);
        }
      }}
      onBlur={(event) => {
        /*
          Only a genuine focus-out commits. The calendar button is inside this
          wrapper and the calendar itself is portalled out of it, so both the
          reach for the button and the step into the open calendar have to be
          read as *staying*. Otherwise the editor commits and closes while the
          reader is mid-gesture, and the date they then click lands on a row
          that is no longer open.

          A `null` `relatedTarget` is a click on something unfocusable (the
          page background, or another row's read-out), and that is leaving.
        */
        const next = event.relatedTarget;
        /*
          `instanceof Element` rather than a truthiness check: `relatedTarget`
          is typed as `EventTarget | null` and really can be neither. jsdom
          hands back the `Document` when focus falls off the end of the page,
          and `document.closest` does not exist.
        */
        const stayed =
          next instanceof Element &&
          (event.currentTarget.contains(next) ||
            next.closest('[data-radix-popper-content-wrapper]') !== null);
        if (stayed) return;
        onCommit(text);
      }}
    >
      <DatePicker
        date={parsed}
        /*
          `toIsoDateOnly`, never a raw `format(next, 'yyyy-MM-dd')`. The
          picker yields **local midnight**, and in Australia that is the
          previous day in UTC — so any serialisation that routes through
          `toISOString()` reports a date one day early for every user in the
          country. That helper exists for exactly this, with the footgun in
          its own doc comment; restating the format string here would be a
          second definition of one fact (root `CLAUDE.md`, *bind to the source
          of truth*).
        */
        onDateChange={(next) => onCommit(next ? toIsoDateOnly(next) : '')}
        placeholder={attributePlaceholder(field.label)}
        ariaLabel={field.label}
        yearNavigation
        allowInput
        /*
          Bare like every other unified editor — the cell has the chrome, so a
          bordered input inside the bordered cell is the double outline.
          `pr-7` leaves room for the calendar button the wrapper above has
          pulled in to `right-0`.
        */
        className={cn(
          ATTRIBUTE_VALUE_TEXT,
          ATTRIBUTE_LEADING,
          'h-auto py-1 pr-7 pl-1.5',
          ATTRIBUTE_EDITOR_BARE
        )}
      />
    </div>
  );
}
